import { ButtonAlignment, AccountType, type Session } from '@nextgen-web-framework/all';
import {
  profileApi,
  RegFlowConfig,
  DynamicObjectInterface,
  RegistrationStatus,
  AccountDetail,
  Status,
  AccountDetailRenewalStatusEnum,
  AccountDetailRenewalLifecycleEnum,
  AccountLifeCycleStatusType,
  ProcessAccountLifeCycleType,
  Citizenship,
  ProfileMapping,
  CMS_CONTENT_TYPE,
  AccountProcessType,
  AccountLifeCycleCMSDataProps,
  getAccountLifeCycleCmsDataTransformer,
  AccountLifeCycleResult,
  TermsConditionType,
  Profile,
  AccountAutoRenew,
  ProfileConstantsEnum,
  type AutoRenewalAttributesType,
  type RegistrationResponse,
  type AccountLifeCycleConfigType,
  RenewalMemberConfigType,
  RenewalConfigType,
  RenewalPeriodTypeEnum,
  Projection,
  Subscription,
  AutoRenewalDeliveryTypeEnum,
  Contact,
  ContactType,
  PaymentStatus,
  AutoRenewalOrderStatus,
  FunctionType,
} from '@commerce-profile-webcomponents/sdk';
import { cmsApi } from '@commerce-webcomponents/sdk';
import { PaymentDetails, PaymentEntryType, PaymentTransactionStatus } from '@commerce-payment-webcomponents/sdk';
import { DateFormats, Logger, StorageService } from '@nextgen-web-framework/core';
import { OrderDeliveryTypeEnum } from '@commerce-order-webcomponents/sdk';
import { productApi, ProjectionType } from '@commerce-product-webcomponents/sdk';
import logObject from '../../logObject';
import {
  parseNumber,
  parseString,
  parseObject,
  formatDateByLocale,
  defaultApiErrorHandler,
  getEnv,
  parseArray,
  iff,
  parseDate,
  convertToLocalDate,
  parseLocalDate,
} from './helper';
import { ErrorConstants, AccountLifeCycleConstants } from './Constants';
import { CountryInterface } from './masterdata';
import { getConfigByPK, getInputConfig, MasterConfigNameConst } from './myAccountProfileUtils';
import { MyAccountData, MyAccountInput } from './types/propTypes';

export const invalidRegistrationObject = {
  responseBody: {
    code: ErrorConstants.GENERAL_ERROR_CODE,
  },
};

export const getRegistration = async (
  regFlowType?: RegFlowConfig,
  aboNum?: string,
  status: RegistrationStatus = RegistrationStatus.Started,
  skipError = false,
  apiErrorHandler = defaultApiErrorHandler,
  isReturnObject = false,
): Promise<DynamicObjectInterface | string> => {
  try {
    const { registrations } = await apiErrorHandler(() =>
      profileApi.getRegistration({
        accountNumber: parseInt(parseString(aboNum)),
        status: status,
        ...(regFlowType && { registrationFlow: regFlowType }),
      }),
    );
    const [registration] = registrations || [];
    const { registrationId = '' } = parseObject(registration);
    return iff(isReturnObject, registration, registrationId);
  } catch (error: DynamicObjectInterface) {
    if (skipError) {
      // use for co-app to check if registration exist
      // return empty string if registration not found
      return '';
    } else {
      throw error ?? invalidRegistrationObject;
    }
  }
};

export const getRegistrationById = async (
  registrationId: string,
  projection: Projection = Projection.Small,
  apiErrorHandler = defaultApiErrorHandler,
): Promise<RegistrationResponse> => {
  try {
    const registration = await apiErrorHandler(() =>
      profileApi.getRegistrationDetailsById(registrationId, {}, projection),
    );
    return registration;
  } catch {
    return {} as unknown as RegistrationResponse;
  }
};

export const generateRegistrationId = async (regFlowType: RegFlowConfig, apiErrorHandler = defaultApiErrorHandler) => {
  try {
    const { registrationId = '' } = await apiErrorHandler(() =>
      profileApi.createRegistrationId(
        {
          registrationFlow: regFlowType,
        } as unknown as DynamicObjectInterface,
        {},
        true,
      ),
    );
    return registrationId;
  } catch (error: DynamicObjectInterface) {
    if (error?.responseBody) {
      // to handle other error codes
      throw error;
    }
    throw invalidRegistrationObject;
  }
};

export const getAccountData = async (accountNumber: string, apiErrorHandler = defaultApiErrorHandler) => {
  try {
    const accountInfoRes = await apiErrorHandler(() => profileApi.getAccountInformation(accountNumber));
    return accountInfoRes;
  } catch {
    return {
      name: {
        name: '',
      },
      expirationDate: '',
    } as unknown as AccountDetail;
  }
};

export const getAccountAutoRenewal = async (
  accountNumber: string,
  apiErrorHandler = defaultApiErrorHandler,
): Promise<AutoRenewalAttributesType> => {
  try {
    const res = await apiErrorHandler(() => profileApi.getAccountAutoRenewal(accountNumber));
    const { autoRenewDetails } = res;
    const autoRenewEnabled = autoRenewDetails?.signedUpFlag ?? false;

    const sessionStorage = StorageService.getSessionStorage();
    const autoRenewalAttributes = {
      lastUpdate: new Date().toISOString(),
      autoRenewEnabled,
    } as AutoRenewalAttributesType;
    sessionStorage.store(ProfileConstantsEnum.accountRenewalAttributes, autoRenewalAttributes);

    return {
      autoRenewEnabled,
      autoRenewDetails: autoRenewDetails,
    };
  } catch {
    // If account not found, return default object
    // No error message required
    return {
      autoRenewEnabled: false,
      autoRenewDetails: {
        signedUpFlag: false,
        subscription: {
          subscriptionDeliveryList: [
            {
              deliveryTypeCd: '',
              warehouseCd: '',
            },
          ],
        },
      },
    } as unknown as AccountAutoRenew;
  }
};

export const getAllAccounts = async (accountNum: string, apiErrorHandler = defaultApiErrorHandler) => {
  const res = await apiErrorHandler(() => profileApi.getAccountByNum(accountNum));
  const { profileList } = res ?? { profileList: [] };
  return profileList;
};

export const getCitizenshipByCountry = (
  currentCountry: CountryInterface,
  citizenshipCountryCode?: string,
  citizenship?: string,
) => {
  if (citizenshipCountryCode) {
    return citizenshipCountryCode?.toLowerCase() === String(currentCountry?.pk)?.toLowerCase()
      ? Citizenship.Citizen
      : Citizenship.Foreigner;
  }
  // fallback to default citizenship
  return parseString(citizenship, Citizenship.Citizen);
};

export const getAccountCitizenship = async (
  aboNum: string,
  profileId: string,
  accountType: AccountType,
  citizenship: string,
  apiErrorHandler = defaultApiErrorHandler,
) => {
  if (accountType === AccountType.Member) {
    const profileList = await getAllAccounts(aboNum, apiErrorHandler);
    const primaryProfile = profileList.find((profile: ProfileMapping) => profile.primary);

    // check if profileId is primary
    if (primaryProfile?.profileId === profileId) {
      return citizenship;
    } else if (primaryProfile?.profileId) {
      const primaryProfileInfo = await getProfileById(primaryProfile?.profileId, apiErrorHandler);
      return parseString(primaryProfileInfo?.citizenship);
    }
  }

  return '';
};

export const getProfileById = async (profileId: string, apiErrorHandler = defaultApiErrorHandler): Promise<Profile> => {
  try {
    const profile = await apiErrorHandler(() => profileApi.getProfilesById(profileId));
    return profile;
  } catch {
    // 2DO: To add error handling  when profile not found
    return {} as unknown as Profile;
  }
};

export const getModalButtonAlignment = (isDesktop: boolean) =>
  isDesktop ? ButtonAlignment.HORIZONTAL : ButtonAlignment.VERTICAL;

const defaultLifeCycleConfig: AccountLifeCycleConfigType = {
  allowRenewal: false,
  renewalConfig: {
    renewalPeriodType: RenewalPeriodTypeEnum.EXPIRY_DATE,
  },
  allowAutoRenewal: false,
  allowAutoRenewalCheck: false,
  allowUpgrade: false,
  upgradeConfig: {
    validCitizenCountryCode: '',
    upgradeMinAge: 0,
  },
  allowDowngrade: false,
  downgradeConfig: {
    validStatus: [],
    maxPinLevel: 0,
    earlyDowngradeRestriction: {
      days: 0,
    },
  },
  useProfileCart: false,
  autoRenewalConfig: {},
};

export const calculateRenewalDates = (
  lifeCycleConfig: AccountLifeCycleConfigType,
  accountExpirationDate: string,
  entryDateString: string,
  locale: string,
  dateFormat: DateFormats,
) => {
  const { allowRenewal, renewalConfig } = lifeCycleConfig;
  const { renewalPeriodType, showFullDate, config } = renewalConfig as RenewalConfigType;
  const correctedDate = parseDate(accountExpirationDate);
  const correctedEntryDate = parseDate(entryDateString);
  if (!allowRenewal || !config) {
    return {
      allowRenewal: false,
      renewalStartDate: correctedDate,
      renewalNudgeStartDate: correctedDate,
      gracePeriodDate: correctedDate,
      isLastGraceMonth: false,
      formattedRenewalStartDate: '',
      formattedExpiryDate: formatDateByLocale(correctedDate.toString(), locale, dateFormat),
      formattedGracePeriodDate: correctedDate.toISOString(),
      entryDate: correctedEntryDate,
      isRenewalMonth: false,
      isExpired: false,
      showNudgeDuringAutoRenewalMonth: false,
    };
  }

  const currentDate = new Date(); // TODO: Adjust date for testing

  // Convert date to market timezone for calculation purpose
  const expDate = convertToLocalDate(accountExpirationDate);
  const entryDate = convertToLocalDate(entryDateString);

  const renewalStartDate = new Date(expDate);
  renewalStartDate.setDate(1); // start of the month
  let renewalNudgeStartDate = new Date(renewalStartDate);

  // Actual Expiry Date - use for display, we can use framework function to convert it directly
  const expiryDate = correctedDate;
  const formattedExpiryDate = formatDateByLocale(expiryDate?.toString(), locale, dateFormat);

  const gracePeriodDate = new Date(expDate);
  let isLastGraceMonth = false;
  const { showNudgeDuringAutoRenewalMonth } = config;

  if (renewalPeriodType === RenewalPeriodTypeEnum.FIXED_MONTH) {
    const { renewalMonthStart, renewalMonthEnd } = config;

    renewalStartDate.setMonth(parseNumber(renewalMonthStart) - 1);
    renewalNudgeStartDate.setMonth(parseNumber(renewalMonthStart) - 1);

    gracePeriodDate.setFullYear(renewalStartDate.getFullYear() + 1); // set to next year
    gracePeriodDate.setMonth(parseNumber(renewalMonthEnd), 1); // set to first day of the month
    gracePeriodDate.setDate(0); // last day of previous month
  } else if (renewalPeriodType === RenewalPeriodTypeEnum.EXPIRY_DATE) {
    const { renewalWindowPeriodMonths, renewalGracePeriodMonths, alertOnLastGraceMonth, renewalNudgeBeginMonths } =
      config;

    renewalStartDate.setMonth(renewalStartDate.getMonth() - parseNumber(renewalWindowPeriodMonths));
    gracePeriodDate.setMonth(gracePeriodDate.getMonth() + parseNumber(renewalGracePeriodMonths) + 1);
    gracePeriodDate.setDate(0); // last day of previous month

    if (renewalNudgeBeginMonths) {
      renewalNudgeStartDate.setMonth(renewalNudgeStartDate.getMonth() - parseNumber(renewalNudgeBeginMonths));
    } else {
      renewalNudgeStartDate = new Date(renewalStartDate);
    }

    if (alertOnLastGraceMonth && gracePeriodDate.getMonth() === currentDate.getMonth()) {
      isLastGraceMonth = true;
    }
  }

  // For calculation: convert date to mm/dd/yyyy
  const localizedYearDate = formatDateByLocale(renewalStartDate.toString(), locale, DateFormats.SHORT_DATE);
  const localizedYear = String(localizedYearDate).slice(-4);

  // For display - to convert to YYYY-MM-DD without timezone information, then format it
  const tempISODate = `${renewalStartDate.getFullYear()}-${String(renewalStartDate.getMonth() + 1).padStart(2, '0')}-${String(renewalStartDate.getDate()).padStart(2, '0')}`;
  const tempLocalDate = parseLocalDate(tempISODate);
  const localizedStartDate = formatDateByLocale(tempLocalDate, locale, DateFormats.SHORT_DATE);

  const formattedRenewalStartDate = showFullDate
    ? localizedStartDate
    : `${renewalStartDate.getMonth() + 1}/${localizedYear}`;

  const formattedGracePeriodDate = formatDateByLocale(gracePeriodDate.toString(), locale, dateFormat);

  const isRenewalMonth =
    expDate.getMonth() === currentDate.getMonth() && expDate.getFullYear() === currentDate.getFullYear();
  const isExpired = expDate.getMonth() < currentDate.getMonth();

  return {
    allowRenewal,
    renewalStartDate,
    renewalNudgeStartDate,
    gracePeriodDate,
    isLastGraceMonth,
    formattedRenewalStartDate,
    formattedExpiryDate,
    formattedGracePeriodDate,
    entryDate,
    isRenewalMonth,
    isExpired,
    showNudgeDuringAutoRenewalMonth,
  };
};

export const getAccountStatusChangeFlag = (
  renewalLifecycle?: AccountDetailRenewalLifecycleEnum,
  businessNatureLifeCycle?: AccountDetailRenewalLifecycleEnum,
  allowRenewal?: boolean,
) => {
  const isRenewalLifeCycleValid =
    !renewalLifecycle || (renewalLifecycle && renewalLifecycle !== AccountDetailRenewalLifecycleEnum.Processing);
  const isBusinessNatureLifeCycleValid =
    !businessNatureLifeCycle ||
    (businessNatureLifeCycle && businessNatureLifeCycle !== AccountDetailRenewalLifecycleEnum.Completed);
  const lifecycleFlag = allowRenewal && isRenewalLifeCycleValid && isBusinessNatureLifeCycleValid;

  return Boolean(lifecycleFlag);
};

export const checkValidPinLevel = (pinLevel?: number, maxPinLevel?: number) => {
  if (maxPinLevel && pinLevel) {
    return pinLevel < parseNumber(maxPinLevel);
  }
  return true;
};

export const checkValidStatus = (
  status?: AccountDetailRenewalStatusEnum,
  validStatus?: AccountDetailRenewalStatusEnum | AccountDetailRenewalStatusEnum[],
) => {
  if (validStatus) {
    return iff(
      Array.isArray(validStatus),
      validStatus.includes(status as AccountDetailRenewalStatusEnum),
      validStatus === status,
    );
  }
  return true;
};

export const processMemberTypeConfig = (
  memberConfig: RenewalMemberConfigType,
  renewalStatus: AccountDetailRenewalStatusEnum,
  entryDate?: Date,
  pinLevel?: number,
  enablePinCheck = false, // enable pin check for renewal and downgrade
): DynamicObjectInterface => {
  const { allowLateRenew } = parseObject(memberConfig);

  const returnObj = (flag: boolean, isEarlyDowngrade = false) => ({
    allowFlag: flag,
    // additional flags
    earlyDowngradeRestriction: isEarlyDowngrade,
    allowLateRenew,
  });

  if (!memberConfig) {
    // No additional config for downgrade, thus return true
    return returnObj(true, false);
  }

  const { validStatus, maxPinLevel, earlyDowngradeRestriction } = memberConfig as unknown as RenewalMemberConfigType;

  // Check is valid status
  const isValidStatus = checkValidStatus(renewalStatus, validStatus);
  if (!isValidStatus) {
    return returnObj(false);
  }

  // Check max pin level
  const isValidPinLevel = checkValidPinLevel(pinLevel, maxPinLevel);
  if (enablePinCheck && !isValidPinLevel) {
    return returnObj(false);
  }

  // For Downgrade Only -->
  // Check early downgrade restriction
  let earlyDowngradeRestrictionFlag = false;
  if (earlyDowngradeRestriction?.days && entryDate) {
    const currentDate = new Date(); // TODO: Adjust date for testing
    const numberOfDays = entryDate
      ? Math.floor((currentDate.getTime() - new Date(entryDate).getTime()) / (1000 * 60 * 60 * 24))
      : 0;
    earlyDowngradeRestrictionFlag = numberOfDays < parseNumber(earlyDowngradeRestriction.days);
  }
  // <-- For Downgrade Only

  return returnObj(true, earlyDowngradeRestrictionFlag);
};

export const processLifeCycleData = ({
  type,
  accountExpirationDate,
  entryDate: entryDateString = '', // for downgrade
  pinLevel, // for renewal and downgrade
  renewalStatus,
  accountStatus,
  locale,
  dateFormat,
  renewalLifecycle,
  businessNatureLifeCycle,
  lifeCycleConfig,
  isAutoRenewalEnabled = false, // Required for renewal nudge only
  isAutoRenewalFailed = false, // Required for renewal nudge only
  isAutoRenewalOrderCancelled = false, // Required for renewal nudge only
}: ProcessAccountLifeCycleType): AccountLifeCycleStatusType => {
  const currentDate = new Date(); // TODO: Adjust date for testing

  const {
    allowRenewal,
    renewalStartDate,
    renewalNudgeStartDate,
    isLastGraceMonth,
    formattedRenewalStartDate,
    formattedExpiryDate,
    formattedGracePeriodDate,
    entryDate,
    isRenewalMonth,
    isExpired,
  } = calculateRenewalDates(lifeCycleConfig, accountExpirationDate, entryDateString, locale, dateFormat);

  // Get from config
  const { renewalConfig, downgradeConfig, disableManualRenewalForDiamond, disableDowngradeForPlatinum } =
    lifeCycleConfig;
  const { config: renewalMemberConfig } = parseObject(renewalConfig) as unknown as DynamicObjectInterface;
  const { allowFlag: renewalFlag, allowLateRenew } = processMemberTypeConfig(
    renewalMemberConfig,
    renewalStatus,
    entryDate,
    pinLevel,
    disableManualRenewalForDiamond, // enable for pin check
  );

  const { config: downgradeMemberConfig } = parseObject(downgradeConfig) as unknown as DynamicObjectInterface;
  const { allowFlag: downgradeFlag, earlyDowngradeRestriction } = processMemberTypeConfig(
    downgradeMemberConfig,
    renewalStatus,
    entryDate,
    pinLevel,
    disableDowngradeForPlatinum, // enable for pin check
  );

  const isABO = type === AccountType.BusinessOwner;
  const validShowRenewalDate = renewalNudgeStartDate.getTime() <= currentDate.getTime();
  const validRenewalDate = renewalStartDate.getTime() <= currentDate.getTime();

  const accountStatusChangeFlag =
    [AccountDetailRenewalStatusEnum.CanRenew, AccountDetailRenewalStatusEnum.MustRenew].includes(
      parseString(renewalStatus) as DynamicObjectInterface,
    ) && accountStatus === Status.Active;

  const lifecycleFlag = getAccountStatusChangeFlag(renewalLifecycle, businessNatureLifeCycle, allowRenewal);

  const canRenew = lifecycleFlag && renewalFlag && validRenewalDate && accountStatusChangeFlag;
  const showRenew = lifecycleFlag && renewalFlag && validShowRenewalDate && accountStatusChangeFlag;
  const mustRenew = lifecycleFlag && renewalFlag && renewalStatus === AccountDetailRenewalStatusEnum.MustRenew;

  const canLateRenew = allowLateRenew && mustRenew; // both mustRenew and allowLateRenew are true

  // Renewal Nudge
  let showRenewalNudge = false;
  if (isAutoRenewalEnabled) {
    // BETA launch - show renewal nudge after end of expiry month
    // To consider Diamond/Platinum to show the renewal nudge
    showRenewalNudge =
      (renewalFlag && accountStatusChangeFlag && (isAutoRenewalOrderCancelled || (mustRenew && isExpired))) ||
      isAutoRenewalFailed;
  } else {
    showRenewalNudge = mustRenew || showRenew;
  }

  return {
    expiryDate: formattedExpiryDate,
    rawExpiryDate: accountExpirationDate, // return original date
    inactiveDate: formattedGracePeriodDate,
    renewalStartDate: formattedRenewalStartDate,
    mustRenew,
    canRenew,
    canLateRenew,
    canUpgrade: !isABO && accountStatus === Status.Active && lifecycleFlag,
    canDowngrade: isABO && lifecycleFlag && downgradeFlag,
    isEarlyDowngrade: earlyDowngradeRestriction,
    isAutoRenewalEnabled,
    isAutoRenewalFailed: isAutoRenewalFailed,
    showRenewalNudge,
    isABO,
    type,
    isLastGraceMonth: isLastGraceMonth && showRenewalNudge,
    isRenewalMonth,
    isExpired,
  };
};

export const getAccountLifeCycleTermsAndConditions = async (
  processType: AccountProcessType,
  apiErrorHandler = defaultApiErrorHandler,
): Promise<TermsConditionType> => {
  const emptyResponse = {
    title: '',
    description: '',
  };
  try {
    const cmsResponse: DynamicObjectInterface = await apiErrorHandler(() =>
      cmsApi.fetchContent({
        uId: CMS_CONTENT_TYPE.ACCOUNT_LIFE_CYCLE,
      }),
    );

    const results: AccountLifeCycleCMSDataProps = getAccountLifeCycleCmsDataTransformer(
      cmsResponse as AccountLifeCycleResult,
    );

    let returnResponse: DynamicObjectInterface;
    switch (processType) {
      case AccountProcessType.Renewal:
        returnResponse = results?.data?.renewalTermsCondition;
        break;
      case AccountProcessType.Upgrade:
        returnResponse = results?.data?.upgradeTermsCondition;
        break;
      case AccountProcessType.Downgrade:
        returnResponse = results?.data?.downgradeTermsCondition;
        break;
      default:
        returnResponse = emptyResponse;
    }

    return returnResponse ?? emptyResponse;
  } catch {
    return emptyResponse;
  }
};

export const initialAccountLifeCycleStatus = {
  expiryDate: '',
  rawExpiryDate: '',
  inactiveDate: '',
  renewalStartDate: '',
  mustRenew: false,
  canRenew: false,
  canLateRenew: false,
  canUpgrade: false,
  canDowngrade: false,
  isAutoRenewalEnabled: false,
  isAutoRenewalFailed: false,
  showRenewalNudge: false,
  isABO: false,
  type: AccountType.Member,
  isRenewalMonth: false,
  isExpired: false,
};

/**
 * Fetches and processes the account renewal banner status.
 *
 * @param session - The session object containing user information.
 * @param myAccountConfig - The configuration data for the account.
 * @param locale - The locale string for localization.
 * @param stateHandling - The function to handle the state.
 * @param useCache - Optional. A flag to indicate whether to use the cache. Defaults to false.
 * @returns A promise that resolves to the account life cycle status.
 */
export const accountRenewalBannerStatus = async (
  session: Session,
  myAccountConfig: MyAccountData[],
  featureFlags: DynamicObjectInterface,
  locale: string,
  stateHandling?: FunctionType | null,
  useCache = false,
  apiErrorHandler = defaultApiErrorHandler,
): Promise<AccountLifeCycleStatusType> => {
  const { aboNum, currentAwardLevel } = session;
  const [shortLocaleCode] = String(locale).split('-');
  let responseObject: AccountLifeCycleStatusType = initialAccountLifeCycleStatus;

  if (!aboNum) {
    stateHandling?.(responseObject);
    return responseObject;
  }

  const accountRes = await getAccountData(aboNum, apiErrorHandler);
  const { accountType, expirationDate, renewalStatus, renewalLifecycle, status, autoRenewalOrderStatus } =
    parseObject(accountRes);
  const expDate = parseString(expirationDate);
  const usrAutoRenewalOrderStatus = parseString(autoRenewalOrderStatus);

  // TODO: Temporary solution before merging Date Enhancement PR (1348)
  const generalConfig = getConfigByPK(
    myAccountConfig,
    MasterConfigNameConst.STEP_GENERAL_CONFIG,
  ) as DynamicObjectInterface;
  // const dateFormat =
  //   DateFormats[(generalConfig?.defaultDateFormat as keyof typeof DateFormats) ?? DateFormats.SHORT_DATE];
  // const dateLocale = parseString(generalConfig?.dateLocale, shortLocaleCode);
  const dateLocale = generalConfig?.dateFormat?.transformation.find(
    (item: DynamicObjectInterface) => item?.siteLocale === shortLocaleCode,
  )?.dateLocale;

  // Lifecycle Config
  const { inputs: lifeCycleConfig } = getConfigByPK(myAccountConfig, MasterConfigNameConst.STEP_ACCOUNT_LIFE_CYCLE);

  const configs = extractLifeCycleConfig(lifeCycleConfig, accountType, featureFlags);
  const { allowAutoRenewalCheck, enableAutoRenewal } = configs;

  const isAutoRenewalFailed = usrAutoRenewalOrderStatus === AutoRenewalOrderStatus.Failed;
  const isAutoRenewalOrderCancelled = usrAutoRenewalOrderStatus === 'CANCELLED'; // wait for new enum: AutoRenewalOrderStatus.Cancelled;
  let isAutoRenewalEnabled = false,
    flagUpdated = false;

  if (allowAutoRenewalCheck || enableAutoRenewal) {
    if (useCache) {
      // To check when it's canRenew
      const sessionStorage = StorageService.getSessionStorage();

      const renewalAttributes = sessionStorage.retrieve(
        ProfileConstantsEnum.accountRenewalAttributes,
      ) as unknown as AutoRenewalAttributesType;

      const { lastUpdate, autoRenewEnabled } = renewalAttributes || {};

      if (lastUpdate) {
        isAutoRenewalEnabled = !!autoRenewEnabled;

        flagUpdated = true;
      } else {
        // To call to get AutoRenewalStatus
        const renewalRes = await getAccountAutoRenewal(aboNum, apiErrorHandler);
        isAutoRenewalEnabled = !!renewalRes?.autoRenewEnabled;
      }
    }
    if (!flagUpdated) {
      // To call to get AutoRenewalStatus
      const { autoRenewEnabled } = await getAccountAutoRenewal(aboNum, apiErrorHandler);
      isAutoRenewalEnabled = !!autoRenewEnabled;
    }
  }

  responseObject = processLifeCycleData({
    type: accountType,
    isAutoRenewalEnabled,
    isAutoRenewalFailed,
    isAutoRenewalOrderCancelled,
    accountExpirationDate: String(expDate),
    renewalStatus: parseString(renewalStatus) as AccountDetailRenewalStatusEnum,
    renewalLifecycle: parseString(renewalLifecycle) as AccountDetailRenewalLifecycleEnum,
    accountStatus: status,
    locale: dateLocale ?? shortLocaleCode,
    dateFormat: DateFormats.SHORT_DATE,
    lifeCycleConfig: configs,
    pinLevel: parseInt(parseString(currentAwardLevel)),
  });

  stateHandling?.(responseObject);
  return responseObject;
};

export const getRenewalConfig = (accountLifeCycleConfig: MyAccountInput[], configType: string) => {
  // Get renewal config
  const renewalConfig = getInputConfig(accountLifeCycleConfig, configType);
  if (renewalConfig) {
    return renewalConfig;
  } else {
    return {
      visible: false,
      renewalPeriodType: RenewalPeriodTypeEnum.EXPIRY_DATE,
      showFullDate: false,
      memberType: [],
    };
  }
};

export const getConfigByMemberType = (memberType?: RenewalMemberConfigType[], accountType?: string) => {
  const env = getEnv();
  const memberConfig = memberType?.find(
    (item: RenewalMemberConfigType) =>
      item.memberType === accountType && ((Array.isArray(item?.env) && item.env.includes(env)) || !item?.env),
  );
  return memberConfig;
};

export const extractLifeCycleConfig = (
  accountLifeCycleConfig?: MyAccountInput[],
  accountType?: string,
  featureFlags?: DynamicObjectInterface,
): AccountLifeCycleConfigType => {
  if (!accountLifeCycleConfig || accountLifeCycleConfig.length === 0) {
    return defaultLifeCycleConfig;
  }

  const {
    enableRenewal,
    enableUpgrade,
    enableDowngrade,
    enableAutoRenewal,
    enableAutoRenewalCheck,
    // enableAllowRenewOnAutoRenewalMonth,
    // enableAllowUpdateOnAutoRenewalMonth,
    disableManualRenewalForDiamond,
    disableDowngradeForPlatinum,
  } = featureFlags;

  const renewalConfig = getRenewalConfig(accountLifeCycleConfig, AccountLifeCycleConstants.RENEWAL_CONFIG);
  const memberConfig = getConfigByMemberType((renewalConfig as DynamicObjectInterface)?.memberType, accountType);

  // Get auto-renewal config
  const autoRenewalConfig = getInputConfig(accountLifeCycleConfig, AccountLifeCycleConstants.ALLOW_AUTO_RENEWAL);

  // Get upgrade config
  const upgradeConfig = getInputConfig(accountLifeCycleConfig, AccountLifeCycleConstants.ALLOW_UPGRADE);
  const { validCitizenCountryCode: validCitizenCountry, minor } = upgradeConfig;

  // Get downgrade config
  const downgradeConfig = getInputConfig(accountLifeCycleConfig, AccountLifeCycleConstants.ALLOW_DOWNGRADE);
  const downgradeMemberConfig = getConfigByMemberType(
    (downgradeConfig as DynamicObjectInterface)?.memberType,
    accountType,
  );

  // Get other config
  const createCartConfig = getInputConfig(accountLifeCycleConfig, AccountLifeCycleConstants.USE_PROFILE_CART);

  return {
    allowRenewal: enableRenewal,
    renewalConfig: {
      renewalPeriodType: parseString(renewalConfig?.renewalPeriodType) as RenewalPeriodTypeEnum,
      showFullDate: Boolean(renewalConfig?.showFullDate),
      config: memberConfig,
    },
    allowAutoRenewal: enableAutoRenewal,
    allowAutoRenewalCheck: enableAutoRenewalCheck,
    allowUpgrade: enableUpgrade,
    upgradeConfig: {
      validCitizenCountryCode: String(validCitizenCountry),
      upgradeMinAge: parseNumber((minor as DynamicObjectInterface)?.min),
      upgradeMaxAge: parseNumber((minor as DynamicObjectInterface)?.max),
    },
    allowDowngrade: enableDowngrade,
    downgradeConfig: {
      // maintain same format as renewalConfig
      config: downgradeMemberConfig,
    },
    useProfileCart: Boolean(createCartConfig?.value),
    autoRenewalConfig: autoRenewalConfig,
    disableManualRenewalForDiamond,
    disableDowngradeForPlatinum,
  };
};

// Utils for useAutoRenewal
export const autoRenewalInitFeeData = async (
  skuConfig: DynamicObjectInterface,
  apiErrorHandler = defaultApiErrorHandler,
) => {
  const defaultResponse = {
    currency: '',
    totalAmount: 0,
    formattedPrice: '',
  };

  if (!skuConfig?.fee) {
    return defaultResponse;
  }

  try {
    const productRes = await apiErrorHandler(() =>
      productApi.getProductByIds({
        idList: [parseString(skuConfig?.fee)],
        projections: ProjectionType.Detailed,
      }),
    );
    const [product] = parseArray(productRes?.products);
    const [item] = parseArray(product?.items);
    const [price] = parseArray(item?.prices);
    return {
      currency: parseString(price?.currency),
      totalAmount: parseNumber(price?.priceIncludingTax),
      formattedPrice: parseString(price?.formattedPrice),
    };
  } catch {
    return defaultResponse;
  }
};

export const autoRenewalGetExistingRegistration = async (accountNumber: string, registrationId?: string) => {
  let regId = registrationId;

  if (!regId) {
    const existingRegId = await getRegistration(
      RegFlowConfig.AutoRenewSignUpFlow,
      accountNumber,
      RegistrationStatus.Started,
      true,
    );

    regId = existingRegId;
  }

  if (regId) {
    const registrationDetails = await getRegistrationById(regId, Projection.Full);
    return registrationDetails;
  }

  return null;
};

export const autoRenewalTransformToSubscription = (
  isEkit: boolean,
  isHomeDelivery: boolean,
  pickupLocationId?: string | null,
  selectedKitSku?: string,
  locale?: string,
  autoRenewSubscriptionId?: string,
  autoRenewSubscriptionName?: string,
): Subscription => {
  const deliveryType = isHomeDelivery ? AutoRenewalDeliveryTypeEnum.HOME_DELIVERY : AutoRenewalDeliveryTypeEnum.PICKUP;
  const delv = isEkit ? AutoRenewalDeliveryTypeEnum.EKIT : deliveryType;
  const warehouseCd = delv === AutoRenewalDeliveryTypeEnum.PICKUP ? pickupLocationId : null;

  // MDMS DeliveryCode
  // http://dms-service-dev:8080/DMS_Service_Web/DomainCodeLookUp.jsp?code=DLVRY
  const mdmsDlvyCd = isEkit ? AutoRenewalDeliveryTypeEnum.DMS_EMAIL : AutoRenewalDeliveryTypeEnum.DMS_POSTAL_MAIL;

  return {
    subscriptionId: autoRenewSubscriptionId,
    subscriptionName: autoRenewSubscriptionName,
    skuAliasId: selectedKitSku,
    subscriptionDeliveryList: [
      {
        deliveryTypeCd: mdmsDlvyCd,
        deliveryChoiceCd: 'Y',
        languageCd: locale,
        ...(warehouseCd && { warehouseCd }),
      },
    ],
  };
};

export const autoRenewalTransformFromSubscription = (sub: Subscription): DynamicObjectInterface => {
  const { skuAliasId, subscriptionDeliveryList } = parseObject(sub);
  const [deliveryData] = parseArray(subscriptionDeliveryList);

  const { deliveryTypeCd, warehouseCd } = parseObject(deliveryData);

  return {
    feeSku: skuAliasId,
    deliveryTypeCd,
    warehouseCd,
    isEkit:
      deliveryTypeCd === AutoRenewalDeliveryTypeEnum.EKIT || deliveryTypeCd === AutoRenewalDeliveryTypeEnum.DMS_EMAIL,
    deliveryType:
      deliveryTypeCd === AutoRenewalDeliveryTypeEnum.PICKUP || warehouseCd
        ? OrderDeliveryTypeEnum.BOPIS
        : OrderDeliveryTypeEnum.HOMEDELIVERY,
    serviceLocationId: warehouseCd,
  };
};

export const autoRenewalStartProcess = async (
  accountNumber: string,
  registrationId?: string,
  reuseRegistration = false,
): Promise<RegistrationResponse> => {
  const logger = Logger.getInstance(logObject('accountLifeCycleUtils.ts', 'autoRenewalStartProcess'));
  try {
    // Check for existing registration
    let existingReg = null;
    if (reuseRegistration && registrationId) {
      existingReg = await autoRenewalGetExistingRegistration(accountNumber, registrationId);
    }

    if (existingReg) {
      return existingReg;
    } else {
      // Start registration process
      const registrationId = await generateRegistrationId(RegFlowConfig.AutoRenewSignUpFlow);
      return {
        registrationId,
      };
    }
  } catch (error: DynamicObjectInterface) {
    logger.error('Error while starting registration process', error);

    if (error?.responseBody) {
      // to handle other error codes
      throw error;
    }
    throw invalidRegistrationObject;
  }
};

export const autoRenewalGetDefaultAddress = async (
  aboNumber: string,
  profileId: string,
  contactType: ContactType = ContactType.Shipping,
  apiErrorHandler = defaultApiErrorHandler,
) => {
  const addressRes = await apiErrorHandler(() =>
    profileApi.getShippingAddressList({
      contactType,
      ownerId: profileId,
      accountNumber: aboNumber,
    }),
  );

  if (addressRes) {
    const { contacts } = addressRes;
    return contacts?.find((addr: Contact) => addr.isDefault);
  } else {
    return null;
  }
};

export const autoRenewalValidatePaymentDetails = (paymentDetails: PaymentDetails): boolean => {
  if (!paymentDetails || paymentDetails.status !== PaymentStatus.INITIATED || !paymentDetails.transactions?.length) {
    return false;
  }

  const [transaction] = paymentDetails.transactions;
  if (!transaction || transaction.status !== PaymentTransactionStatus.Initiated || !transaction.entries?.length) {
    return false;
  }

  return transaction.entries[0]?.entryType === PaymentEntryType.Initiate;
};
